import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useScrollVelocity } from "@/hooks/use-scroll-velocity";
import { useLibrarySearch } from "@/lib/library-search";
import { getLibraryFirstPage } from "@/lib/library.functions";
import { freshnessOf } from "@/lib/library-types";
import type { LibraryGem, SortMode } from "@/lib/library-types";
import { LibraryCard } from "@/components/library-card";
import { SiteFooter } from "@/components/site-footer";
import { BEST_LISTS } from "@/lib/library-best";
import { CATEGORY_INTRO, hubSlug } from "@/lib/library-hubs";
import { LibrarySkeletonGrid } from "@/components/library-skeleton";
import { AgentPromptHint } from "@/components/agent-prompt-hint";
import { WebMcpLibraryTools } from "@/components/webmcp-provider";
import { WebMcpIndicator } from "@/components/webmcp-indicator";
import { Gem } from "@/components/icons";



export const Route = createFileRoute("/library/")({
  head: ({ loaderData }) => ({
    meta: [
      {
        title: "Hidden Gems Library — 2,000+ curated dev & design resources | Vibing Company",
      },
      {
        name: "description",
        content:
          "A hand-curated library of 2,000+ design components, dev tools, AI skills, shaders, and inspiration. Search instantly by name, tag, or category — no signup needed.",
      },
      { property: "og:title", content: "Hidden Gems Library — curated dev & design resources" },
      ...ogImageMeta("library", "Share card: Hidden Gems Library"),
      {
        property: "og:description",
        content:
          "2,000+ hand-picked design components, dev tools, AI skills and more. Instant fuzzy search, beautiful cards, no signup.",
      },
      { property: "og:url", content: "https://vibing.inc/library" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "ai-usage",
        content:
          "Free to query. Search API: https://vibing.inc/api/public/library/search?q= — MCP: https://vibing.inc/api/public/mcp — docs: https://vibing.inc/for-agents",
      },
    ],
    links: [
      { rel: "canonical", href: "https://vibing.inc/library" },
      // Agent discovery: the plain-text index, the machine API description and
      // the ready-to-paste prompt all hang off the page itself.
      { rel: "alternate", type: "text/plain", href: "https://vibing.inc/llms.txt", title: "llms.txt" },
      { rel: "alternate", type: "text/plain", href: "https://vibing.inc/library/prompt.txt", title: "Agent prompt" },
      { rel: "alternate", type: "text/markdown", href: "https://vibing.inc/md/library", title: "Markdown" },
      { rel: "service-desc", type: "application/json", href: "https://vibing.inc/api/public/openapi.json" },
      // Start the search index download alongside the HTML instead of after
      // hydration, and get the first row of previews in flight immediately.
      { rel: "preload", as: "fetch", href: "/api/public/library", crossOrigin: "anonymous" },
      ...((loaderData?.firstPage ?? [])
        .slice(0, 4)
        .filter((g: LibraryGem) => !!g.img)
        .map((g: LibraryGem) => ({
          rel: "preload",
          as: "image",
          href: g.img,
        }))),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "The Vibeary — Hidden Gems Library",
          url: "https://vibing.inc/library",
          description:
            "The biggest public vibe coding library: 2,000+ curated design components, dev tools, AI skills, shaders and inspiration.",
          isPartOf: { "@type": "WebSite", name: "Vibing Company", url: "https://vibing.inc" },
          potentialAction: [
            {
              "@type": "SearchAction",
              target: {
                "@type": "EntryPoint",
                urlTemplate: "https://vibing.inc/api/public/library/search?q={query}",
                contentType: "application/json",
                httpMethod: "GET",
              },
              "query-input": "required name=query",
            },
            {
              "@type": "SearchAction",
              target: "https://vibing.inc/library?q={query}",
              "query-input": "required name=query",
            },
          ],
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://vibing.inc/" },
              { "@type": "ListItem", position: 2, name: "Library", item: "https://vibing.inc/library" },
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: "The Vibing Library",
          description:
            "A continuously curated dataset of 2,000+ design tools, UI components, shaders, AI skills and starters, each with category, tags, licence, author and source URL.",
          url: "https://vibing.inc/library",
          license: "https://vibing.inc/terms",
          isAccessibleForFree: true,
          creator: {
            "@type": "Organization",
            name: "the Vibing Company",
            url: "https://vibing.inc",
          },
          keywords: [
            "design tools",
            "UI components",
            "shaders",
            "AI skills",
            "MCP servers",
            "vibe coding",
          ],
          distribution: [
            {
              "@type": "DataDownload",
              encodingFormat: "application/json",
              contentUrl: "https://vibing.inc/api/public/library/search",
            },
            {
              "@type": "DataDownload",
              encodingFormat: "text/plain",
              contentUrl: "https://vibing.inc/llms-full.txt",
            },
          ],
        }),
      },
    ],
  }),

  loader: async () => {
    try {
      return { firstPage: (await getLibraryFirstPage()) as LibraryGem[] };
    } catch {
      return { firstPage: [] as LibraryGem[] };
    }
  },

  component: LibraryPage,
});

const PAGE_SIZE = 36;
const FAST_PAGE_SIZE = 72;
const MAX_RENDERED = 600;
const SCROLL_KEY = "vibeary:scroll";

function LibraryPage() {
  const { firstPage } = Route.useLoaderData();
  const { index, results, total, loading, ready, search, getGem } =
    useLibrarySearch(firstPage);

  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [subcategory, setSubcategory] = useState<string | null>(null);
  const [sort, setSort] = useState<SortMode>("relevance");
  const [renderCount, setRenderCount] = useState(PAGE_SIZE);
  // Bumped whenever the query genuinely changes, so the grid can replay its
  // staggered entrance without animating on infinite-scroll appends.
  const [epoch, setEpoch] = useState(0);
  const [docked, setDocked] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const heroSentinel = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const velocity = useScrollVelocity();
  const restored = useRef(false);
  const lastFilterKey = useRef<string | null>(null);

  // WebMCP: when an agent searches the library, mirror it into the visible UI
  // so the human sees exactly what the agent asked for.
  const applyAgentFilters = useCallback(
    (f: {
      query?: string;
      category?: string | null;
      subcategory?: string | null;
      sort?: SortMode;
    }) => {
      startTransition(() => {
        if (f.query !== undefined) setQ(f.query);
        if (f.category !== undefined) setCategory(f.category);
        if (f.subcategory !== undefined) setSubcategory(f.subcategory);
        if (f.sort) setSort(f.sort);
      });
    },
    [],
  );


  // Sync query to URL for shareable searches.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const uq = params.get("q");
    const uc = params.get("cat");
    const us = params.get("sub");
    const uo = params.get("sort");
    if (uq) setQ(uq);
    if (uc) setCategory(uc);
    if (us) setSubcategory(us);
    if (uo && ["relevance", "name", "views", "likes", "new"].includes(uo)) {
      setSort(uo as SortMode);
    }

    // Instant back-navigation: restore how far the user had scrolled and how
    // many cards were mounted when they left for a gem page.
    try {
      const raw = sessionStorage.getItem(SCROLL_KEY);
      if (raw) {
        const saved = JSON.parse(raw) as {
          y: number;
          count: number;
          q: string;
          cat: string | null;
        };
        if (saved.q === (uq ?? "") && saved.cat === (uc ?? null)) {
          restored.current = true;
          setRenderCount(Math.min(saved.count || PAGE_SIZE, MAX_RENDERED));
          // Wait until the restored cards have actually laid out before
          // jumping, otherwise the document is still too short to scroll.
          const target = saved.y;
          const started = performance.now();
          let cancelled = false;
          const stop = () => {
            cancelled = true;
          };
          // Any real user input wins over restoration.
          window.addEventListener("wheel", stop, { once: true, passive: true });
          window.addEventListener("touchstart", stop, { once: true, passive: true });
          window.addEventListener("keydown", stop, { once: true });
          const tryScroll = () => {
            if (cancelled) return;
            if (Math.abs(window.scrollY - target) > 4) window.scrollTo(0, target);
            // Hold the position across the page-transition frames, which
            // otherwise reset the window to the top after we land.
            if (performance.now() - started < 3000) requestAnimationFrame(tryScroll);
            else {
              window.removeEventListener("wheel", stop);
              window.removeEventListener("touchstart", stop);
              window.removeEventListener("keydown", stop);
            }
          };
          requestAnimationFrame(tryScroll);
        }
      }
    } catch {
      // sessionStorage can be unavailable; restoration is best-effort.
    }
  }, []);

  // Persist grid position while scrolling (rAF-throttled) so leaving for a
  // gem page — which scrolls the window — can't erase it. Mount-only: the
  // live values are read from a ref so the listener is never re-created.
  const stateRef = useRef({ renderCount, q, category });
  stateRef.current = { renderCount, q, category };

  useEffect(() => {
    let ticking = false;
    // Frozen once the user commits to a gem card; the click itself moves the
    // page, so later scroll events are not a reading of where they were.
    let frozen = false;
    const save = () => {
      ticking = false;
      if (frozen || window.scrollY <= 0) return;
      try {
        sessionStorage.setItem(
          SCROLL_KEY,
          JSON.stringify({
            y: window.scrollY,
            count: stateRef.current.renderCount,
            q: stateRef.current.q,
            cat: stateRef.current.category,
          }),
        );
      } catch {
        // ignore
      }
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(save);
    };
    // A pointer down on a gem card is the last honest reading of where the
    // user was: snapshot it and stop tracking, because the click itself moves
    // the page before unmount.
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest?.("a[href^='/library/']")) return;
      save();
      frozen = true;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("pagehide", save);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("pagehide", save);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      search(q, category, subcategory, sort);
      // Only collapse the grid when the filters genuinely changed — the hook
      // re-runs on internal state flips too, and that must not wipe a
      // restored back-navigation position.
      const key = `${q}|${category ?? ""}|${subcategory ?? ""}|${sort}`;
      if (lastFilterKey.current !== key) {
        lastFilterKey.current = key;
        setEpoch((e) => e + 1);
        if (restored.current) restored.current = false;
        else setRenderCount(PAGE_SIZE);
      }

    }, q.length > 2 ? 40 : 0);
  }, [q, category, subcategory, sort, search]);

  // Update URL when filters change (debounced).
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (category) params.set("cat", category);
      if (subcategory) params.set("sub", subcategory);
      if (sort !== "relevance") params.set("sort", sort);
      const qs = params.toString();
      const url = qs ? `/library?${qs}` : "/library";
      window.history.replaceState(null, "", url);
    }, 300);
    return () => clearTimeout(timer);
  }, [q, category, subcategory, sort]);

  // Predictive infinite scroll: the faster the fling, the further ahead we
  // look and the bigger the batch, so the grid is never caught empty.
  // Appending happens off the scroll thread (idle + transition).
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    let obs: IntersectionObserver | null = null;
    let margin = -1;
    let pending = false;

    const append = () => {
      if (pending) return;
      pending = true;
      const grow = velocity.current > 1200 ? FAST_PAGE_SIZE : PAGE_SIZE;
      const run = () => {
        startTransition(() =>
          setRenderCount((c) => Math.min(c + grow, MAX_RENDERED)),
        );
        pending = false;
        // The sentinel may still sit inside the lookahead band after the
        // append; re-observing forces a fresh callback so a long fling keeps
        // filling instead of stalling on a single intersection event.
        requestAnimationFrame(() => {
          if (!obs) return;
          obs.unobserve(el);
          obs.observe(el);
        });
      };
      const ric = (
        window as Window & {
          requestIdleCallback?: (cb: IdleRequestCallback, o?: { timeout: number }) => number;
        }
      ).requestIdleCallback;
      if (ric) ric(() => run(), { timeout: 200 });
      else requestAnimationFrame(run);
    };

    const attach = (rootMargin: number) => {
      if (margin === rootMargin) return;
      margin = rootMargin;
      obs?.disconnect();
      obs = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) append();
        },
        { rootMargin: `${rootMargin}px 0px` },
      );
      obs.observe(el);
    };

    attach(800);

    // Re-tune the lookahead as velocity changes (cheap timer, no re-render).
    const tune = setInterval(() => {
      const v = velocity.current;
      const next = v > 3000 ? 3200 : v > 1500 ? 2000 : v > 500 ? 1400 : 800;
      attach(next);
    }, 250);

    return () => {
      clearInterval(tune);
      obs?.disconnect();
    };
  }, [results, velocity]);
  // Dock the search field under the nav once the hero has scrolled past.
  // Scroll-position based with hysteresis: an IntersectionObserver on a
  // sentinel oscillates here, because docking collapses the hero and pushes
  // the sentinel back into view, which immediately undocks it again.
  useEffect(() => {
    const DOCK_AT = 150;
    const UNDOCK_AT = 90;
    let frame = 0;
    const evaluate = () => {
      frame = 0;
      const y = window.scrollY;
      setDocked((prev) => (prev ? y > UNDOCK_AT : y > DOCK_AT));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(evaluate);
    };
    evaluate();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);


  // Keyboard: "/" or ⌘K focuses search, Esc clears it, arrows walk the grid.
  useEffect(() => {
    const isTyping = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      const tag = el?.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable;
    };

    const onKey = (e: KeyboardEvent) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        return;
      }
      if (e.key === "/" && !isTyping(e.target)) {
        e.preventDefault();
        inputRef.current?.focus();
        return;
      }
      if (e.key === "Escape" && isTyping(e.target) && e.target === inputRef.current) {
        setQ("");
        return;
      }
      if (!e.key.startsWith("Arrow") || isTyping(e.target)) return;
      const grid = gridRef.current;
      const active = document.activeElement as HTMLElement | null;
      if (!grid || !active?.matches?.("a[data-gem]")) return;
      const cards = Array.from(grid.querySelectorAll<HTMLElement>("a[data-gem]"));
      const i = cards.indexOf(active);
      if (i === -1) return;
      // Column count derived from the real layout, so it follows the breakpoint.
      const top = cards[0].getBoundingClientRect().top;
      let cols = cards.findIndex((c) => c.getBoundingClientRect().top > top + 4);
      if (cols <= 0) cols = cards.length;
      const step =
        e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : e.key === "ArrowDown" ? cols : -cols;
      const next = cards[i + step];
      if (!next) return;
      e.preventDefault();
      next.focus({ preventScroll: false });
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Before the index is ready, show the server-rendered first page so the
  // grid is never blank; afterwards the worker's results drive it.
  const visibleGems = useMemo(() => {
    if (!ready) return firstPage.slice(0, renderCount);
    const out: LibraryGem[] = [];
    for (let i = 0; i < Math.min(renderCount, results.length); i++) {
      const gem = getGem(results[i]);
      if (gem) out.push(gem);
    }
    return out;
  }, [ready, firstPage, results, renderCount, getGem]);

  const totalMatches = ready ? results.length : firstPage.length;
  const showSkeletons = visibleGems.length === 0 && (loading || !ready);

  // Today's drops: the staff picks added in the last 24 hours. Only shown on
  // the unfiltered view, so a search never competes with it.
  const freshGems = useMemo(() => {
    const rows = index?.rows;
    if (!rows) {
      return firstPage.filter((g) => freshnessOf(g.addedDay) === "today").slice(0, 4);
    }
    const out: LibraryGem[] = [];
    for (let i = 0; i < rows.length && out.length < 4; i++) {
      if (freshnessOf(rows[i][9] ?? 0) !== "today") continue;
      const gem = getGem(i);
      if (gem) out.push(gem);
    }
    return out;
  }, [index, firstPage, getGem]);

  // Subcategories available inside the active category, most common first.
  const subOptions = useMemo(() => {
    if (!category || !index) return [] as { name: string; count: number }[];
    const catCode = index.cats.indexOf(category);
    if (catCode < 0) return [];
    const counts = new Map<string, number>();
    for (const r of index.rows) {
      if (r[3] !== catCode) continue;
      const name = index.subs[r[4]] ?? "";
      if (!name) continue;
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [category, index]);

  const showFresh = freshGems.length > 0 && !q.trim() && !category && !subcategory;


  return (
    <div className="min-h-dvh bg-background text-foreground">
      <WebMcpLibraryTools applyFilters={applyAgentFilters} />
      {docked && <TopBlurVeil />}
      <main id="main" tabIndex={-1}>
        {/* Text-mode agents that ignore JSON-LD still find the API here. */}
        <p className="sr-only">
          This catalogue is machine-readable and free to query. Search API:
          https://vibing.inc/api/public/library/search?q=&lt;text&gt;. One gem:
          https://vibing.inc/api/public/library/gems/&lt;slug&gt;. MCP server:
          https://vibing.inc/api/public/mcp. Copy-paste prompt:
          https://vibing.inc/library/prompt.txt.
        </p>
        <section
          aria-labelledby="lib-h"
          className="relative mx-auto max-w-[1400px] px-6 md:px-10"
        >
          <LibraryHero total={index?.rows.length ?? 0} />
          <div ref={heroSentinel} aria-hidden className="h-px" />

          <h2 id="lib-h" className="sr-only">Search the library</h2>

          <div
            className={`sticky z-30 pb-2 ${
              docked ? "top-2 md:top-3" : "top-[3.25rem] md:top-[4.75rem]"
            }`}
          >
            <div
              className={`rounded-3xl bg-background/85 backdrop-blur-xl ring-1 ring-border/60 shadow-[0_1px_2px_rgba(20,20,40,0.05),0_18px_50px_-32px_rgba(20,20,40,0.45)] transition-[padding,box-shadow,background-color,--tw-ring-color] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              docked ? "p-3 md:p-4" : "p-4 md:p-5"
            }`}
            >
              <SearchField
                q={q}
                setQ={setQ}
                inputRef={inputRef}
                docked={docked}
                resultCount={total}
                loading={loading}
                hasQuery={q.trim().length > 0}
              />

              <FilterBar
                categories={index?.categories ?? []}
                subOptions={subOptions}
                category={category}
                subcategory={subcategory}
                sort={sort}
                onCategory={(c) => {
                  setCategory(c);
                  setSubcategory(null);
                }}
                onSubcategory={setSubcategory}
                onSort={setSort}
              />
            </div>
          </div>


          {showFresh ? (
            <section aria-labelledby="fresh-h" className="mt-14">
              <div className="flex items-baseline justify-between gap-4 border-b border-border/60 pb-3">
                <h2
                  id="fresh-h"
                  className="font-display text-xl tracking-[-0.02em] text-foreground"
                >
                  Picks of the day
                </h2>
                <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                  {freshGems.length} added today
                </p>
              </div>
              <div className="mt-5 gap-5 sm:columns-2 lg:columns-3 xl:columns-4 [&>*]:mb-5 [&>*]:break-inside-avoid">
                {freshGems.map((gem) => (
                  <LibraryCard
                    key={`fresh-${gem.slug}`}
                    slug={gem.slug}
                    name={gem.name}
                    description={gem.description}
                    category={gem.category}
                    subcategory={gem.subcategory}
                    tags={gem.tags}
                    author={gem.author}
                    license={gem.license}
                    ogImageUrl={gem.img}
                    addedDay={gem.addedDay}
                    views={gem.views}
                    likes={gem.likes}
                    eager
                  />
                ))}
              </div>
            </section>
          ) : null}

          <div
            ref={gridRef}
            key={epoch}
            className="mt-14 gap-5 sm:columns-2 lg:columns-3 xl:columns-4 pb-20 [&>*]:mb-5 [&>*]:break-inside-avoid"
          >
            {showSkeletons ? <LibrarySkeletonGrid count={12} /> : null}

            {visibleGems.map((gem, i) => (
              <LibraryCard
                key={gem.slug}
                slug={gem.slug}
                name={gem.name}
                description={gem.description}
                category={gem.category}
                subcategory={gem.subcategory}
                tags={gem.tags}
                author={gem.author}
                license={gem.license}
                ogImageUrl={gem.img}
                addedDay={gem.addedDay}
                views={gem.views}
                likes={gem.likes}
                query={q}
                eager={i < 4}
              />
            ))}
          </div>


          {visibleGems.length === 0 && !showSkeletons ? (
            <EmptyState q={q} onClear={() => setQ("")} />
          ) : null}

          {renderCount < totalMatches ? (
            <div
              ref={sentinelRef}
              className="flex justify-center py-12"
              aria-hidden
            >
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-foreground/40" />
            </div>
          ) : null}

        </section>

        <CollectionLinks />
      </main>

      <SiteFooter />
    </div>
  );
}

/** Rolls a number toward its new value instead of snapping. */
function useTweenedNumber(value: number) {
  const [shown, setShown] = useState(value);
  const from = useRef(value);

  useEffect(() => {
    const start = performance.now();
    const a = from.current;
    const b = value;
    if (a === b) return;
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / 320);
      const eased = 1 - Math.pow(1 - p, 3);
      const next = Math.round(a + (b - a) * eased);
      setShown(next);
      from.current = next;
      if (p < 1) raf = requestAnimationFrame(tick);
      else from.current = b;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return shown;
}

function LibraryHero({ total }: { total: number }) {
  return (
    <div className="pt-[clamp(3rem,6vh,5rem)] pb-10">

      <div className="flex flex-wrap items-start justify-between gap-3">
        <Link
          to="/"
          className="group inline-flex items-center gap-2 rounded-full py-1.5 pl-2 pr-4 text-sm text-muted-foreground ring-1 ring-border/60 transition-[color,background-color,box-shadow,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:text-foreground hover:ring-foreground/20 hover:bg-muted/40 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Return home"
        >
          <span
            aria-hidden
            className="grid h-7 w-7 place-items-center rounded-full bg-muted text-foreground transition-[transform,background-color] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-x-0.5 group-hover:bg-foreground group-hover:text-background"
          >
            <svg viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5">
              <path
                d="M10 3 4.5 8l5.5 5"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Back home
        </Link>
        <AgentPromptHint total={total} />
        <WebMcpIndicator />
      </div>
      <h1
        className="mt-7 font-display tracking-[-0.035em] leading-[1.02] max-w-[20ch] flex items-center gap-3"
        style={{ fontSize: "clamp(2.4rem, 6vw, 5rem)" }}
      >
        <Gem
          aria-hidden
          className="text-foreground"
          style={{ width: "0.72em", height: "0.72em" }}
        />
        The Vibing Library
      </h1>
      <p className="mt-5 max-w-[52ch] text-lg md:text-xl leading-snug text-muted-foreground text-pretty">
        Our ever growing library with the best and newest tools, curated by the
        Vibing Co.'s staff daily through Reddit, X and Discord.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
        <Link
          to="/library/submit"
          className="inline-flex min-h-[36px] items-center rounded-full bg-foreground px-4 text-background transition-opacity hover:opacity-90"
        >
          Submit a project
        </Link>
        <span className="text-muted-foreground">
          Found your project?{" "}
          <Link to="/library/badge" className="text-foreground underline underline-offset-4">
            Grab the “Featured in the Vibing Library” badge
          </Link>
        </span>
      </div>
    </div>

  );
}

/**
 * Progressive blur band: several stacked layers whose blur strength rises
 * toward the nav, each masked so it fades out downward. Content scrolling up
 * dissolves gradually instead of clipping at a hard line.
 */
function TopBlurVeil() {
  const layers = [
    { blur: "2px", from: 0, to: 40 },
    { blur: "5px", from: 15, to: 60 },
    { blur: "10px", from: 35, to: 80 },
    { blur: "20px", from: 55, to: 100 },
  ];
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-20 h-[calc(5rem+env(safe-area-inset-top))] md:h-[7.5rem]"
    >
      {layers.map((l) => (
        <div
          key={l.blur}
          className="absolute inset-0"
          style={{
            backdropFilter: `blur(${l.blur})`,
            maskImage: `linear-gradient(to bottom, rgb(0 0 0) ${l.from}%, rgb(0 0 0 / 0) ${l.to}%)`,
          }}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-transparent" />
    </div>
  );
}

const SUGGESTIONS = [
  "shaders",
  "claude skills",
  "scroll animation",
  "icons",
  "3d",
  "typography",
];

const PLACEHOLDERS = [
  "shaders",
  "Claude skills",
  "scroll animations",
  "open-source icon sets",
  "React components",
];

/** Cycles example searches through the placeholder until the user engages. */
function useTypewriter(active: boolean) {
  const [text, setText] = useState("");
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    if (!active) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setText(PLACEHOLDERS[0]);
      return;
    }
    const word = PLACEHOLDERS[wordIndex % PLACEHOLDERS.length];
    let i = 0;
    let deleting = false;
    let timer: ReturnType<typeof setTimeout>;

    const step = () => {
      if (!deleting) {
        i++;
        setText(word.slice(0, i));
        if (i === word.length) {
          deleting = true;
          timer = setTimeout(step, 1600);
          return;
        }
      } else {
        i--;
        setText(word.slice(0, i));
        if (i === 0) {
          setWordIndex((w) => w + 1);
          return;
        }
      }
      timer = setTimeout(step, deleting ? 28 : 55);
    };

    timer = setTimeout(step, 220);
    return () => clearTimeout(timer);
  }, [active, wordIndex]);

  return text;
}

function SearchField({
  q,
  setQ,
  inputRef,
  docked,
  resultCount,
  loading,
  hasQuery,
}: {
  q: string;
  setQ: (v: string) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  docked: boolean;
  resultCount: number;
  loading: boolean;
  hasQuery: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const typed = useTypewriter(!hasQuery && !focused);
  const shown = useTweenedNumber(resultCount);

  return (
    <div>
      <div
        className={`flex w-full items-center gap-3 transition-[padding] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          docked ? "py-1 pl-2 pr-2" : "py-2 pl-3 pr-2"
        }`}
      >

        <svg
          aria-hidden
          className={`pointer-events-none shrink-0 text-muted-foreground transition-[width,height] duration-500 ${
            docked ? "h-4 w-4" : "h-5 w-5"
          }`}
          viewBox="0 0 20 20"
          fill="none"
        >
          <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.5" />
          <path d="m14 14 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>

        <div className="relative min-w-0 flex-1">
          {!hasQuery && !focused ? (
            <span
              aria-hidden
              className={`pointer-events-none absolute inset-y-0 left-0 flex items-center text-muted-foreground/70 ${
                docked ? "text-base" : "text-base md:text-lg"
              }`}
            >
              Search&nbsp;{typed}
              <span className="ml-px inline-block h-[1.1em] w-px translate-y-[1px] bg-muted-foreground/60" />
            </span>
          ) : null}
          <input
            ref={inputRef}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            aria-label="Search the library"
            placeholder={focused && !hasQuery ? "Search 2,000+ gems…" : ""}
            className={`w-full bg-transparent py-1 text-foreground placeholder:text-muted-foreground/70 focus:outline-none [&::-webkit-search-cancel-button]:appearance-none ${
              docked ? "text-base" : "text-base md:text-lg"
            }`}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        {hasQuery ? (
          <button
            type="button"
            onClick={() => {
              setQ("");
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-muted-foreground transition-[color,background-color,transform] duration-200 hover:bg-muted hover:text-foreground active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden>
              <path d="m6 6 8 8M14 6l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        ) : (
          <kbd className="mr-1 hidden shrink-0 rounded-md border border-border/70 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:block">
            /
          </kbd>
        )}

        <span
          className="shrink-0 pr-2 text-xs tabular-nums text-muted-foreground"
          aria-live="polite"
        >
          {loading ? "…" : `${shown.toLocaleString()} ${hasQuery ? "matches" : "gems"}`}
        </span>
      </div>

    </div>
  );

}


const SORTS: { value: SortMode; label: string }[] = [
  { value: "relevance", label: "Most relevant" },
  { value: "views", label: "Most viewed" },
  { value: "likes", label: "Most liked" },
  { value: "new", label: "Newest" },
  { value: "name", label: "A–Z" },
];

/** Category and subcategory chips plus the sort control. */
function FilterBar({
  categories,
  subOptions,
  category,
  subcategory,
  sort,
  onCategory,
  onSubcategory,
  onSort,
}: {
  categories: { name: string; count: number }[];
  subOptions: { name: string; count: number }[];
  category: string | null;
  subcategory: string | null;
  sort: SortMode;
  onCategory: (c: string | null) => void;
  onSubcategory: (s: string | null) => void;
  onSort: (s: SortMode) => void;
}) {
  if (categories.length === 0) return null;

  const chip = (active: boolean) =>
    `shrink-0 rounded-full px-3.5 py-1.5 text-sm transition-[color,background-color,transform] duration-200 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
      active
        ? "bg-foreground text-background"
        : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
    }`;

  return (
    <div>
      <div className="flex items-center gap-3 border-t border-border/40 pt-3">

        <div
          className="-mx-1 flex min-w-0 flex-1 gap-2 overflow-x-auto px-1 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          role="group"
          aria-label="Filter by category"
        >
          <button type="button" className={chip(!category)} onClick={() => onCategory(null)}>
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.name}
              type="button"
              aria-pressed={category === c.name}
              className={chip(category === c.name)}
              onClick={() => onCategory(category === c.name ? null : c.name)}
            >
              {c.name}
              <span className="ml-1.5 text-xs tabular-nums opacity-60">{c.count}</span>
            </button>
          ))}
        </div>

        <label className="relative shrink-0 text-sm">
          <span className="sr-only">Sort results</span>
          <select
            value={sort}
            onChange={(e) => onSort(e.target.value as SortMode)}
            className="cursor-pointer appearance-none rounded-full bg-transparent py-1.5 pl-3 pr-7 text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <svg
            aria-hidden
            viewBox="0 0 16 16"
            fill="none"
            className="pointer-events-none absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground"
          >
            <path d="m4 6.5 4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </label>
      </div>

      {/* Only the active category opens its subcategories, so the default view stays one line. */}
      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          subOptions.length > 1 ? "max-h-16 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div
          className="-mx-1 mt-2 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          role="group"
          aria-label="Filter by subcategory"
        >
          <button
            type="button"
            tabIndex={subOptions.length > 1 ? 0 : -1}
            className={`${chip(!subcategory)} !text-xs !py-1`}
            onClick={() => onSubcategory(null)}
          >
            All {category}
          </button>
          {subOptions.map((s) => (
            <button
              key={s.name}
              type="button"
              aria-pressed={subcategory === s.name}
              className={`${chip(subcategory === s.name)} !text-xs !py-1`}
              onClick={() => onSubcategory(subcategory === s.name ? null : s.name)}
            >
              {s.name}
              <span className="ml-1.5 tabular-nums opacity-60">{s.count}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

}

function EmptyState({ q, onClear }: { q: string; onClear: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <p className="font-display text-2xl text-foreground">Nothing here — yet</p>
      <p className="mt-3 max-w-md text-sm text-muted-foreground">
        {q
          ? `"${q}" didn't match a gem. The library grows daily, so try a broader word — or tell us what's missing.`
          : "Try a broader search term."}
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        {SUGGESTIONS.slice(0, 3).map((s) => (
          <span key={s} className="rounded-full bg-muted/60 px-4 py-2 text-sm text-muted-foreground">
            {s}
          </span>
        ))}
      </div>
      <button
        type="button"
        onClick={onClear}
        className="mt-6 rounded-full px-5 py-2.5 text-sm text-foreground ring-1 ring-border transition-[background-color,transform] duration-200 hover:bg-muted active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        Clear search
      </button>
    </div>
  );
}


/**
 * Crawlable entry points into the category hubs. The grid above is
 * search-driven and virtualised, so these static links are what gives search
 * engines a path from /library into every collection page.
 */
function CollectionLinks() {
  return (
    <section
      aria-labelledby="collections-heading"
      className="mx-auto mt-20 max-w-[1400px] border-t border-border/60 px-6 pt-12 pb-16 md:px-10"
    >
      <div className="grid gap-12 md:grid-cols-2 md:gap-16">
        <div>
          <h2 id="collections-heading" className="font-display text-xl tracking-tight">
            Browse by collection
          </h2>
          <p className="mt-2 max-w-[60ch] text-sm text-muted-foreground">
            Every gem sits on a shelf. Open a collection to see the full list with
            licences, authors and links.
          </p>
          <ul className="mt-6 flex flex-wrap gap-2">
            <li>
              <a
                href="/library/new"
                className="inline-flex min-h-[36px] items-center rounded-full border border-border px-3 text-sm transition-colors hover:bg-muted"
              >
                New this week
              </a>
            </li>
            {Object.keys(CATEGORY_INTRO).map((name) => (
              <li key={name}>
                <a
                  href={`/library/c/${hubSlug(name)}`}
                  className="inline-flex min-h-[36px] items-center rounded-full border border-border px-3 text-sm transition-colors hover:bg-muted"
                >
                  {name}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h2 className="font-display text-xl tracking-tight">Round-ups</h2>
          <p className="mt-2 max-w-[60ch] text-sm text-muted-foreground">
            Opinionated best-of lists for one specific job, with the criteria
            behind each pick.
          </p>
          <ul className="mt-6 flex flex-wrap gap-2">
            <li>
              <a
                href="/library/best"
                className="inline-flex min-h-[36px] items-center rounded-full border border-border px-3 text-sm transition-colors hover:bg-muted"
              >
                All round-ups
              </a>
            </li>
            {BEST_LISTS.map((l) => (
              <li key={l.slug}>
                <a
                  href={`/library/best/${l.slug}`}
                  className="inline-flex min-h-[36px] items-center rounded-full border border-border px-3 text-sm transition-colors hover:bg-muted"
                >
                  {l.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
